import "./sentry";

import * as React from "react";
import * as Sentry from "@sentry/react";
import { StartClient } from "@tanstack/react-start/client";
import { hydrateRoot } from "react-dom/client";

// React 19's error hooks fire for every thrown value the renderer sees,
// including a bare `undefined`. TanStack Router's Match component suspends
// by `throw`ing `router.getMatch(matchId)?._nonReactive.loadPromise`, and
// during transient route transitions (e.g. right after a beforeLoad
// redirect) `getMatch` can return undefined, so the optional-chained
// access evaluates to `undefined` and that bare `undefined` propagates
// out of the suspense boundary. Capturing it gives us a useless
// "Error: undefined" issue with no stack, so drop nullish values before
// forwarding to Sentry. Real exceptions are always Error-like.
function reportToSentry(error: unknown, errorInfo: React.ErrorInfo): void {
  if (error == null) return;
  Sentry.captureReactException(error, errorInfo);
}

// An UNCAUGHT nullish throw means React gave up on the tree: no error boundary
// can represent `undefined`, so the app is left blank (the historical "blank
// onboarding page until refresh" — a manual refresh always recovered it). Do
// that refresh automatically, once per 30s window so a (hypothetical)
// deterministic throw can't reload-loop.
const NULLISH_THROW_RELOAD_KEY = "wonda:nullish-throw-reloaded-at";
const NULLISH_THROW_RELOAD_WINDOW_MS = 30_000;
function recoverFromNullishThrow(): void {
  try {
    const last = Number(sessionStorage.getItem(NULLISH_THROW_RELOAD_KEY) ?? 0);
    if (Date.now() - last < NULLISH_THROW_RELOAD_WINDOW_MS) return;
    sessionStorage.setItem(NULLISH_THROW_RELOAD_KEY, String(Date.now()));
    window.location.reload();
  } catch {
    // Storage unavailable (private mode) — leave the console warning only;
    // reloading unguarded could loop.
  }
}

hydrateRoot(
  document,
  <React.StrictMode>
    <StartClient />
  </React.StrictMode>,
  {
    onUncaughtError: (error, errorInfo) => {
      console.warn("Uncaught error", error, errorInfo.componentStack);
      reportToSentry(error, errorInfo);
      if (error == null) recoverFromNullishThrow();
    },
    onCaughtError: reportToSentry,
    onRecoverableError: reportToSentry,
  },
);
